import React, { useState } from 'react';
import { useApp } from '../contexts/AppContext';
import sharepointConfig from '../config/sharepointConfig';
import { fmtDatum } from '../utils/datum';

function OvertimeTracker() {
  const {
    currentUser, overuren, medewerkers, saldo,
    isAdmin, isManager, canManage,
    registreerOveruren, updateOverurenStatus, updateOveruren,
    berekenOverurenZaterdag, berekenOverurenWerkdag,
  } = useApp();
  const [showForm, setShowForm] = useState(false);
  const [selectedMedewerkerId, setSelectedMedewerkerId] = useState('');
  const [form, setForm] = useState({ Datum: '', startTijd: '', eindTijd: '', Omschrijving: '' });
  const [submitting, setSubmitting] = useState(false);
  const [processing, setProcessing] = useState(null);
  const [error, setError] = useState('');

  // Edit state
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({ startTijd: '', eindTijd: '', uren: '', omschrijving: '' });

  // Payout state
  const [payoutId, setPayoutId] = useState(null);
  const [payoutUren, setPayoutUren] = useState('');

  const beschikbareMedewerkers = medewerkers.filter(m => (m.Status || 'actief') === 'actief' && m.Rol !== 'flexkracht');
  const isVoorAnder = canManage && selectedMedewerkerId && selectedMedewerkerId !== currentUser.id;
  const targetUser = isVoorAnder ? medewerkers.find(m => m.Id === selectedMedewerkerId) : null;

  const isZaterdag = form.Datum ? new Date(form.Datum + 'T12:00:00').getDay() === 6 : false;

  const berekendeUren = () => {
    if (!form.startTijd || !form.eindTijd) return 0;
    return isZaterdag
      ? berekenOverurenZaterdag(form.startTijd, form.eindTijd)
      : berekenOverurenWerkdag(form.startTijd, form.eindTijd);
  };

  const uren = berekendeUren();

  const handleSubmit = async () => {
    if (!form.Datum || !form.startTijd || !form.eindTijd) {
      setError('Vul datum, starttijd en eindtijd in.'); return;
    }
    if (uren <= 0) { setError('Eindtijd moet na starttijd liggen.'); return; }
    try {
      setSubmitting(true);
      const submitData = {
        Datum: form.Datum,
        startTijd: form.startTijd,
        eindTijd: form.eindTijd,
        Uren: uren,
        Omschrijving: form.Omschrijving,
      };
      if (isVoorAnder && targetUser) {
        submitData.medewerkerId = targetUser.Id;
        submitData.medewerkerNaam = targetUser.Naam;
      }
      await registreerOveruren(submitData);
      setForm({ Datum: '', startTijd: '', eindTijd: '', Omschrijving: '' });
      setShowForm(false);
      setError('');
    } catch (err) {
      setError('Fout bij registreren: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusUpdate = async (id, status) => {
    setProcessing(id);
    try { await updateOverurenStatus(id, status); } catch (err) { console.error(err); } finally { setProcessing(null); }
  };

  // Start editing
  const startEdit = (o) => {
    setEditId(o.Id);
    setEditForm({
      startTijd: o.startTijd || '',
      eindTijd: o.eindTijd || '',
      uren: String(o.Uren),
      omschrijving: o.Omschrijving || '',
    });
  };

  // Save edit
  const saveEdit = async (o) => {
    setProcessing(o.Id);
    try {
      const urenNum = parseFloat(String(editForm.uren).replace(',', '.')) || 0;
      if (urenNum <= 0) return;
      await updateOveruren(o.id || o.Id, {
        startTijd: editForm.startTijd,
        eindTijd: editForm.eindTijd,
        uren: urenNum,
        omschrijving: editForm.omschrijving,
      });
      setEditId(null);
    } catch (err) { console.error(err); } finally { setProcessing(null); }
  };

  // Payout with custom hours
  const handlePayout = async (o) => {
    setProcessing(o.Id);
    try {
      const urenNum = parseFloat(String(payoutUren).replace(',', '.'));
      if (!urenNum || urenNum <= 0 || urenNum > o.Uren) return;
      if (urenNum < o.Uren) {
        // Partial: restant behouden als goedgekeurd
        const rest = Math.round((o.Uren - urenNum) * 100) / 100;
        await updateOveruren(o.id || o.Id, {
          uren: rest,
          omschrijving: `${o.Omschrijving || ''} (${urenNum}u uitbetaald)`.trim(),
        });
      } else {
        await updateOverurenStatus(o.Id, 'uitbetaald');
      }
      setPayoutId(null);
      setPayoutUren('');
    } catch (err) { console.error(err); } finally { setProcessing(null); }
  };

  // Data: managers + admin zien alles, medewerkers alleen eigen
  const mijnOveruren = canManage ? overuren : overuren.filter(o => o.medewerkerId === currentUser.id);
  const totalGoedgekeurd = mijnOveruren.filter(o => o.Status === 'goedgekeurd').reduce((s, o) => s + o.Uren, 0);
  const totalIngediend = mijnOveruren.filter(o => o.Status === 'ingediend').reduce((s, o) => s + o.Uren, 0);
  const totalUitbetaald = mijnOveruren.filter(o => o.Status === 'uitbetaald').reduce((s, o) => s + o.Uren, 0);
  const todayStr = new Date().toISOString().split('T')[0];
  const totalToekomstig = mijnOveruren.filter(o => (o.Status === 'goedgekeurd' || o.Status === 'ingediend') && (o.Datum || '') > todayStr).reduce((s, o) => s + o.Uren, 0);

  return (
    <div style={styles.container}>
      <div style={styles.statsRow} className="r-stats">
        <div style={styles.statCard}><div style={styles.statValue} className="stat-val">{totalGoedgekeurd}u</div><div style={styles.statLabel}>Goedgekeurd</div></div>
        <div style={styles.statCard}><div style={{ ...styles.statValue, color: '#FFB900' }}>{totalIngediend}u</div><div style={styles.statLabel}>In afwachting</div></div>
        {totalUitbetaald > 0 && (
          <div style={styles.statCard}><div style={{ ...styles.statValue, color: '#8764B8' }}>{totalUitbetaald}u</div><div style={styles.statLabel}>Uitbetaald</div></div>
        )}
        <div style={styles.statCard}><div style={{ ...styles.statValue, color: '#107C10' }}>{totalGoedgekeurd - totalUitbetaald}u</div><div style={styles.statLabel}>Saldo</div></div>
        {totalToekomstig > 0 && (
          <div style={styles.statCard}><div style={{ ...styles.statValue, color: '#0078D4' }}>{totalToekomstig}u</div><div style={styles.statLabel}>Toekomstig</div></div>
        )}
      </div>

      {/* Zaterdaguren drempel teller — medewerker eigen teller */}
      {(saldo?.zaterdagDrempel || 0) > 0 && !canManage && (() => {
        const drempel = saldo.zaterdagDrempel;
        const gemaakt = saldo.zaterdagUrenGemaakt || 0;
        const gepland = saldo.zaterdagUrenGepland || 0;
        const totaal = gemaakt + gepland;
        const pctGemaakt = Math.min(100, (gemaakt / drempel) * 100);
        const pctGepland = Math.min(100 - pctGemaakt, (gepland / drempel) * 100);
        const alsOveruren = Math.max(0, gemaakt - drempel);
        return (
        <div style={{ background: '#fff', borderRadius: 12, padding: '14px 18px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #edebe9', marginBottom: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: '#323130' }}>📅 Zaterdaguren (onbetaald)</span>
            <span style={{ fontSize: 16, fontWeight: 800, color: totaal >= drempel ? '#107C10' : '#E86E1C' }}>
              {totaal} / {drempel}u
            </span>
          </div>
          <div style={{ background: '#edebe9', borderRadius: 6, height: 12, overflow: 'hidden', display: 'flex' }}>
            {pctGemaakt > 0 && <div style={{
              height: '100%', width: `${pctGemaakt}%`, transition: 'width 0.3s',
              background: 'linear-gradient(90deg, #107C10, #0B6A0B)',
            }} />}
            {pctGepland > 0 && <div style={{
              height: '100%', width: `${pctGepland}%`, transition: 'width 0.3s',
              background: 'linear-gradient(90deg, #D13438, #A4262C)',
            }} />}
          </div>
          <div style={{ display: 'flex', gap: 14, fontSize: 12, color: '#605e5c', marginTop: 6, flexWrap: 'wrap' }}>
            <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: '#107C10', marginRight: 4, verticalAlign: 'middle' }} />Gewerkt: <strong>{gemaakt}u</strong></span>
            {gepland > 0 && <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: '#D13438', marginRight: 4, verticalAlign: 'middle' }} />Gepland: <strong>{gepland}u</strong></span>}
            {alsOveruren > 0 && <span style={{ color: '#107C10' }}>✅ <strong>+{alsOveruren}u</strong> telt als overuren</span>}
            {totaal < drempel && <span>Nog <strong>{drempel - totaal}u</strong> te gaan</span>}
          </div>
        </div>
        );
      })()}

      {/* Zaterdaguren — manager/admin overzicht per medewerker */}
      {canManage && (() => {
        const todayStr = new Date().toISOString().split('T')[0];
        const zaOverzicht = medewerkers
          .filter(m => (m.ZaterdagDrempel || 0) > 0 && (m.Status || 'actief') === 'actief')
          .map(m => {
            const mwOveruren = overuren.filter(o => o.medewerkerId === m.Id);
            const gemaakt = mwOveruren.filter(o => o.Status === 'goedgekeurd' && (o.datum || '') <= todayStr && o.datum && new Date(o.datum + 'T12:00:00').getDay() === 6).reduce((s, o) => s + (o.uren || o.Uren || 0), 0);
            const gepland = mwOveruren.filter(o => (o.Status === 'goedgekeurd' || o.Status === 'ingediend') && (o.datum || '') > todayStr && o.datum && new Date(o.datum + 'T12:00:00').getDay() === 6).reduce((s, o) => s + (o.uren || o.Uren || 0), 0);
            const totaal = gemaakt + gepland;
            const drempel = m.ZaterdagDrempel;
            const bereikt = gemaakt >= drempel;
            const alsOveruren = Math.max(0, gemaakt - drempel);
            const pctG = Math.min(100, (gemaakt / drempel) * 100);
            const pctP = Math.min(100 - pctG, (gepland / drempel) * 100);
            return { naam: m.Naam, id: m.Id, gemaakt, gepland, totaal, drempel, bereikt, alsOveruren, pctG, pctP };
          });
        if (zaOverzicht.length === 0) return null;
        return (
          <div style={{ background: '#fff', borderRadius: 12, padding: '14px 18px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #edebe9', marginBottom: 12 }}>
            <h4 style={{ fontSize: 14, fontWeight: 700, color: '#323130', margin: '0 0 10px 0' }}>📅 Zaterdaguren overzicht</h4>
            {zaOverzicht.map(z => (
              <div key={z.id} style={{ marginBottom: 10, paddingBottom: 10, borderBottom: '1px solid #f3f2f1' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <span style={{ fontSize: 13, fontWeight: 600 }}>{z.naam}</span>
                  <span style={{ fontSize: 13, fontWeight: 800, color: z.bereikt ? '#107C10' : '#E86E1C' }}>
                    {z.totaal}/{z.drempel}u
                    {z.bereikt && <span style={{ color: '#107C10', marginLeft: 6 }}>+{z.alsOveruren}u overuren</span>}
                  </span>
                </div>
                <div style={{ background: '#edebe9', borderRadius: 4, height: 8, overflow: 'hidden', display: 'flex' }}>
                  {z.pctG > 0 && <div style={{
                    height: '100%', width: `${z.pctG}%`, transition: 'width 0.3s',
                    background: 'linear-gradient(90deg, #107C10, #0B6A0B)',
                  }} />}
                  {z.pctP > 0 && <div style={{
                    height: '100%', width: `${z.pctP}%`, transition: 'width 0.3s',
                    background: 'linear-gradient(90deg, #D13438, #A4262C)',
                  }} />}
                </div>
                <div style={{ display: 'flex', gap: 10, fontSize: 11, color: '#605e5c', marginTop: 4 }}>
                  <span><span style={{ display: 'inline-block', width: 7, height: 7, borderRadius: 1, background: '#107C10', marginRight: 3, verticalAlign: 'middle' }} />{z.gemaakt}u gewerkt</span>
                  {z.gepland > 0 && <span><span style={{ display: 'inline-block', width: 7, height: 7, borderRadius: 1, background: '#D13438', marginRight: 3, verticalAlign: 'middle' }} />{z.gepland}u gepland</span>}
                  {z.totaal < z.drempel && <span>· {z.drempel - z.totaal}u te gaan</span>}
                </div>
              </div>
            ))}
          </div>
        );
      })()}

      {!showForm && (
        <button style={styles.addButton} onClick={() => setShowForm(true)}>➕ Overuren registreren</button>
      )}

      {showForm && (
        <div style={styles.formCard} className="r-card">
          <h3 style={styles.formTitle}>Overuren registreren</h3>
          {canManage && (
            <div style={styles.field}>
              <label style={styles.label}>Medewerker</label>
              <select style={styles.input} value={selectedMedewerkerId}
                onChange={e => setSelectedMedewerkerId(e.target.value)}>
                {isAdmin && <option value="" disabled>--- Kies ---</option>}
                {!isAdmin && <option value="">— Eigen overuren —</option>}
                {beschikbareMedewerkers.filter(m => isAdmin ? true : m.Id !== currentUser.id).map(m => (
                  <option key={m.Id} value={m.Id}>{m.Naam}</option>
                ))}
              </select>
              {isVoorAnder && targetUser && (
                <div style={{ fontSize: 12, color: '#0078D4', marginTop: 4 }}>
                  📋 Overuren worden geregistreerd voor <strong>{targetUser.Naam}</strong>
                </div>
              )}
            </div>
          )}
          <div style={styles.formRow} className="r-form-row">
            <div style={styles.field}>
              <label style={styles.label}>Datum</label>
              <input type="date" style={styles.input} value={form.Datum}
                onChange={(e) => setForm({ ...form, Datum: e.target.value })} />
              {form.Datum && (() => {
                const isToekomst = form.Datum > new Date().toISOString().split('T')[0];
                return (
                  <div style={{ fontSize: 12, color: isToekomst ? '#0078D4' : isZaterdag ? '#E86E1C' : '#a19f9d', marginTop: 4, fontWeight: (isZaterdag || isToekomst) ? 600 : 400 }}>
                    {isToekomst ? '🔮 Toekomstig — telt pas mee na de werkdatum' : isZaterdag ? '⚡ Zaterdag — geen pauze-aftrek' : '📅 Werkdag — pauze 12:30-13:00 wordt afgetrokken'}
                  </div>
                );
              })()}
            </div>
          </div>
          <div style={styles.formRow} className="r-form-row">
            <div style={styles.field}>
              <label style={styles.label}>Starttijd</label>
              <input type="time" style={styles.input} value={form.startTijd}
                onChange={(e) => setForm({ ...form, startTijd: e.target.value })} />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Eindtijd</label>
              <input type="time" style={styles.input} value={form.eindTijd}
                onChange={(e) => setForm({ ...form, eindTijd: e.target.value })} />
            </div>
            {uren > 0 && (
              <div style={{ ...styles.field, display: 'flex', alignItems: 'center', paddingTop: 20 }}>
                <div style={{ background: '#f0f6ff', border: '1px solid #c7e0f4', borderRadius: 8, padding: '8px 14px', fontSize: 16, fontWeight: 700, color: '#0078D4' }}>
                  = {Math.round(uren * 100) / 100}u
                </div>
              </div>
            )}
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Opmerkingen (optioneel)</label>
            <input type="text" style={styles.input} value={form.Omschrijving}
              onChange={(e) => setForm({ ...form, Omschrijving: e.target.value })} placeholder="Bijv. levering verwerken..." />
          </div>
          {error && <div style={styles.errorBox}>⚠️ {error}</div>}
          <div style={styles.buttonRow}>
            <button style={styles.cancelBtn} onClick={() => { setShowForm(false); setError(''); }}>Annuleren</button>
            <button style={styles.submitBtn} onClick={handleSubmit} disabled={submitting}>
              {submitting ? 'Bezig...' : isVoorAnder ? `Registreren voor ${targetUser?.Naam?.split(' ')[0]}` : 'Registreren'}
            </button>
          </div>
        </div>
      )}

      <div style={styles.tableCard} className="r-card">
        <h3 style={styles.tableTitle}>Overzicht overuren</h3>
        {mijnOveruren.length === 0 ? (
          <p style={styles.emptyText}>Geen overuren geregistreerd.</p>
        ) : (() => {
          // Per-medewerker zaterdaguren classificatie: onbetaald vs telt als overuren
          const zaterdagStatusMap = {};
          if (canManage) {
            // Manager: bereken per medewerker
            const mwIds = [...new Set(mijnOveruren.map(o => o.medewerkerId))];
            mwIds.forEach(mwId => {
              const mw = medewerkers.find(m => m.Id === mwId);
              const drempel = mw?.ZaterdagDrempel || 0;
              if (drempel <= 0) return;
              const zaEntries = mijnOveruren
                .filter(o => o.medewerkerId === mwId && o.isZaterdag && o.Status === 'goedgekeurd')
                .sort((a, b) => (a.Datum || '').localeCompare(b.Datum || ''));
              let cumulatief = 0;
              zaEntries.forEach(o => {
                const prevCum = cumulatief;
                cumulatief += (o.Uren || 0);
                zaterdagStatusMap[o.Id] = prevCum >= drempel ? 'overuren' : cumulatief <= drempel ? 'onbetaald' : 'gedeeltelijk';
              });
            });
          } else if ((saldo?.zaterdagDrempel || 0) > 0) {
            // Medewerker: eigen entries
            const drempel = saldo.zaterdagDrempel;
            const zaEntries = mijnOveruren
              .filter(o => o.isZaterdag && o.Status === 'goedgekeurd')
              .sort((a, b) => (a.Datum || '').localeCompare(b.Datum || ''));
            let cumulatief = 0;
            zaEntries.forEach(o => {
              const prevCum = cumulatief;
              cumulatief += (o.Uren || 0);
              zaterdagStatusMap[o.Id] = prevCum >= drempel ? 'overuren' : cumulatief <= drempel ? 'onbetaald' : 'gedeeltelijk';
            });
          }

          return (
          <div className="r-table-wrap" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <table style={styles.table}>
            <thead>
              <tr>
                {canManage && <th style={styles.th}>Medewerker</th>}
                <th style={styles.th}>Datum</th>
                <th style={styles.th}>Tijd</th>
                <th style={styles.th}>Uren</th>
                <th style={styles.th}>Opmerking</th>
                {canManage && <th style={styles.th}>Door</th>}
                <th style={styles.th}>Status</th>
                {canManage && <th style={styles.th}>Acties</th>}
              </tr>
            </thead>
            <tbody>
              {mijnOveruren.map((o) => {
                const statusInfo = sharepointConfig.statusOpties.find((s) => s.key === o.Status);
                const ingediendLabel = o.ingediendDoor === o.medewerkerId ? '👤 Zelf'
                  : (medewerkers.find(m => m.Id === o.ingediendDoor)?.IsAdmin ? `👑 ${o.IngediendDoorNaam || 'Beheerder'}`
                    : `📋 ${o.IngediendDoorNaam || 'Manager'}`);
                const isEditing = editId === o.Id;
                const isPayout = payoutId === o.Id;

                return (
                  <tr key={o.Id} style={styles.tr}>
                    {canManage && <td style={styles.td}><strong>{o.MedewerkerNaam}</strong></td>}
                    <td style={styles.td}>
                      {fmtDatum(o.Datum)}
                      {o.isZaterdag && (() => {
                        const zaStatus = zaterdagStatusMap[o.Id];
                        return (
                          <span style={{ fontSize: 10, fontWeight: 600, marginLeft: 4, padding: '1px 5px', borderRadius: 4,
                            background: zaStatus === 'overuren' ? '#107C1015' : zaStatus === 'onbetaald' ? '#8B691415' : '#E86E1C15',
                            color: zaStatus === 'overuren' ? '#107C10' : zaStatus === 'onbetaald' ? '#8B6914' : '#E86E1C',
                          }}>
                            Za{zaStatus === 'overuren' ? ' ✓' : zaStatus === 'onbetaald' ? ' ○' : ''}
                          </span>
                        );
                      })()}
                      {o.Datum > todayStr && <span style={{ fontSize: 9, color: '#0078D4', marginLeft: 4, fontWeight: 600 }}>🔮</span>}
                    </td>

                    {isEditing ? (
                      <>
                        <td style={styles.td}>
                          <div style={{ display: 'flex', gap: 4 }}>
                            <input type="time" value={editForm.startTijd} onChange={e => setEditForm({ ...editForm, startTijd: e.target.value })} style={{ ...styles.editInput, width: 90 }} />
                            <input type="time" value={editForm.eindTijd} onChange={e => setEditForm({ ...editForm, eindTijd: e.target.value })} style={{ ...styles.editInput, width: 90 }} />
                          </div>
                        </td>
                        <td style={styles.td}>
                          <input type="text" value={editForm.uren} onChange={e => setEditForm({ ...editForm, uren: e.target.value })} style={{ ...styles.editInput, width: 50, textAlign: 'center' }} />
                        </td>
                        <td style={styles.td}>
                          <input type="text" value={editForm.omschrijving} onChange={e => setEditForm({ ...editForm, omschrijving: e.target.value })} style={{ ...styles.editInput, width: 120 }} />
                        </td>
                      </>
                    ) : (
                      <>
                        <td style={styles.td}>{o.startTijd && o.eindTijd ? `${o.startTijd}–${o.eindTijd}` : '—'}</td>
                        <td style={styles.td}><strong>{o.Uren}u</strong></td>
                        <td style={styles.td}>{o.Omschrijving}</td>
                      </>
                    )}

                    {canManage && <td style={styles.td}><span style={{ fontSize: 12, color: '#605e5c' }}>{ingediendLabel}</span></td>}
                    <td style={styles.td}>
                      <span style={{ ...styles.badge, background: `${statusInfo?.color}15`, color: statusInfo?.color }}>{statusInfo?.text}</span>
                    </td>

                    {canManage && (
                      <td style={styles.td}>
                        {isEditing ? (
                          <div style={styles.actionRow}>
                            <button style={styles.approveBtn} onClick={() => saveEdit(o)} disabled={processing === o.Id} title="Opslaan">💾</button>
                            <button style={styles.rejectBtn} onClick={() => setEditId(null)} title="Annuleren">✕</button>
                          </div>
                        ) : isPayout ? (
                          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                            <input type="text" value={payoutUren} placeholder={`max ${o.Uren}`}
                              onChange={e => setPayoutUren(e.target.value)}
                              style={{ ...styles.editInput, width: 60, textAlign: 'center' }} />
                            <button style={{ ...styles.approveBtn, background: '#f3e8ff', color: '#8764B8' }} onClick={() => handlePayout(o)} disabled={processing === o.Id} title="Bevestig uitbetaling">💰</button>
                            <button style={styles.rejectBtn} onClick={() => { setPayoutId(null); setPayoutUren(''); }} title="Annuleren">✕</button>
                          </div>
                        ) : (
                          <div style={styles.actionRow}>
                            {o.Status === 'ingediend' && (
                              <>
                                <button style={styles.approveBtn} onClick={() => handleStatusUpdate(o.Id, 'goedgekeurd')} disabled={processing === o.Id} title="Goedkeuren">✓</button>
                                <button style={styles.rejectBtn} onClick={() => handleStatusUpdate(o.Id, 'afgekeurd')} disabled={processing === o.Id} title="Afkeuren">✕</button>
                              </>
                            )}
                            {(o.Status === 'ingediend' || o.Status === 'goedgekeurd') && (
                              <button style={{ ...styles.approveBtn, background: '#E0F2FE', color: '#0369A1' }} onClick={() => startEdit(o)} title="Aanpassen">✎</button>
                            )}
                            {o.Status === 'goedgekeurd' && (
                              <button style={{ ...styles.approveBtn, background: '#f3e8ff', color: '#8764B8' }} onClick={() => { setPayoutId(o.Id); setPayoutUren(String(o.Uren)); }} title="Uitbetalen">💰</button>
                            )}
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        ); })()}
      </div>
    </div>
  );
}

const styles = {
  container: { display: 'flex', flexDirection: 'column', gap: 20 },
  statsRow: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 },
  statCard: { background: '#fff', borderRadius: 12, padding: '20px 24px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', textAlign: 'center' },
  statValue: { fontSize: 32, fontWeight: 700, color: '#0078D4' },
  statLabel: { fontSize: 13, color: '#605e5c', marginTop: 4 },
  addButton: { alignSelf: 'flex-start', background: '#E86E1C', color: '#fff', border: 'none', borderRadius: 10, padding: '10px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  formCard: { background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' },
  formTitle: { fontSize: 16, fontWeight: 600, marginBottom: 16, color: '#1a1a2e' },
  formRow: { display: 'flex', gap: 16, marginBottom: 0 },
  field: { marginBottom: 16, flex: 1, minWidth: 0 },
  label: { display: 'block', fontSize: 13, fontWeight: 600, color: '#323130', marginBottom: 6 },
  input: { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #c8c6c4', fontSize: 14, color: '#323130', boxSizing: 'border-box' },
  editInput: { padding: '4px 6px', borderRadius: 6, border: '1px solid #c8c6c4', fontSize: 12, color: '#323130', boxSizing: 'border-box' },
  errorBox: { background: '#fde7e9', border: '1px solid #f1bbbc', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#a4262c', marginBottom: 16 },
  buttonRow: { display: 'flex', justifyContent: 'flex-end', gap: 12 },
  cancelBtn: { padding: '8px 20px', borderRadius: 8, border: '1px solid #c8c6c4', background: '#fff', fontSize: 14, fontWeight: 600, color: '#605e5c', cursor: 'pointer' },
  submitBtn: { padding: '8px 20px', borderRadius: 8, border: 'none', background: '#E86E1C', fontSize: 14, fontWeight: 600, color: '#fff', cursor: 'pointer' },
  tableCard: { background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', maxWidth: '100%', boxSizing: 'border-box' },
  tableTitle: { fontSize: 16, fontWeight: 600, color: '#1a1a2e', marginBottom: 16 },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '12px 16px', fontSize: 12, fontWeight: 600, color: '#605e5c', borderBottom: '2px solid #edebe9', textTransform: 'uppercase', whiteSpace: 'nowrap' },
  tr: { borderBottom: '1px solid #f3f2f1' },
  td: { padding: '12px 16px', fontSize: 14, color: '#323130', whiteSpace: 'nowrap' },
  badge: { fontSize: 12, fontWeight: 600, padding: '4px 10px', borderRadius: 20 },
  actionRow: { display: 'flex', gap: 6 },
  approveBtn: { width: 32, height: 32, borderRadius: 8, border: 'none', background: '#dff6dd', color: '#107C10', fontSize: 16, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  rejectBtn: { width: 32, height: 32, borderRadius: 8, border: 'none', background: '#fde7e9', color: '#D13438', fontSize: 16, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  emptyText: { fontSize: 14, color: '#a19f9d', textAlign: 'center', padding: 48 },
};

export default OvertimeTracker;
